import { type FullAirHandlerUnit } from "@eisox/api/core-api";

export const getParentAirHandlerUnits = <T extends Pick<FullAirHandlerUnit, "airHandlerUnitParentId">>(
  airHandlerUnits: T[],
): T[] => airHandlerUnits.filter(ahu => ahu.airHandlerUnitParentId === undefined);

export const compareAirHandlerUnitNames = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true });

export const getAreas = (airHandlerUnits: FullAirHandlerUnit[]): FullAirHandlerUnit[] =>
  airHandlerUnits
    .filter(ahu => ahu.airHandlerUnitParentId !== undefined)
    .sort((a, b) => compareAirHandlerUnitNames(a.name, b.name));

interface DuctSensors {
  co2?: number;
  co2Defect?: boolean;
  co2Setpoint?: number;
  co2ActualSetpoint?: number;
  humidity?: number;
  humidityDefect?: boolean;
  humiditySetpoint?: number;
  humidityActualSetpoint?: number;
  flow?: number;
  flowDefect?: boolean;
  flowSetpoint?: number;
  flowActualSetpoint?: number;
  pressure?: number;
  pressureSetpoint?: number;
  pressureActualSetpoint?: number;
  pressureDefect?: boolean;
}

/** A defect only colours the reading it belongs to, so a duct reporting one alone has nothing to draw. */
const DUCT_SENSOR_VALUES = ["co2", "humidity", "flow", "pressure"] as const satisfies readonly (keyof DuctSensors)[];

const DUCT_SENSOR_SETPOINTS = [
  "flowSetpoint",
  "flowActualSetpoint",
  "pressureSetpoint",
  "pressureActualSetpoint",
  "humiditySetpoint",
  "humidityActualSetpoint",
  "co2Setpoint",
  "co2ActualSetpoint",
] as const satisfies readonly (keyof DuctSensors)[];

export const hasDuctValue = (sensors: DuctSensors): boolean =>
  DUCT_SENSOR_VALUES.some(reading => sensors[reading] !== undefined);

export const hasDuctSetpoint = (sensors: DuctSensors): boolean =>
  DUCT_SENSOR_SETPOINTS.some(setpoint => sensors[setpoint] !== undefined);

export const hasDuctReading = (sensors: DuctSensors): boolean => hasDuctValue(sensors) || hasDuctSetpoint(sensors);

export const getDuctSensors = (sensors: DuctSensors): DuctSensors | undefined =>
  hasDuctReading(sensors) ? sensors : undefined;

export type { DuctSensors };

enum SetpointKind {
  ACTUAL = "actual",
  COMPUTED = "computed",
}

interface SetpointLine {
  kind: SetpointKind;
  value: number;
  /** The faded icon marks the second line of a pair; a line standing alone is never faded. */
  muted: boolean;
}

/**
 * The applied setpoint is the one that counts, so it is the line the tile always shows. The
 * computed one only earns a line of its own when it differs -- which is exactly when it says
 * something: a bound, a ramp or a slaved loop is holding the regulation back from its target.
 */
export const getSetpointLines = (actual?: number, computed?: number): SetpointLine[] => {
  if (actual === undefined) {
    return computed === undefined ? [] : [{ kind: SetpointKind.COMPUTED, value: computed, muted: false }];
  }
  if (computed === undefined || computed === actual) {
    return [{ kind: SetpointKind.ACTUAL, value: actual, muted: false }];
  }
  return [
    { kind: SetpointKind.ACTUAL, value: actual, muted: false },
    { kind: SetpointKind.COMPUTED, value: computed, muted: true },
  ];
};

export { SetpointKind };
export type { SetpointLine };
